import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api/client';
import type { Marca } from '../../lib/api/types';
import { useAuth } from '../../lib/auth/AuthContext';
import { useMarcaAtiva } from '../../lib/auth/MarcaAtiva';

/**
 * A casca do painel (A3): lateral, barra superior, seletor de marca.
 *
 * <h3>O seletor de marca só aparece quando há mais de uma</h3>
 * É o ADR-039 na tela. Uma empresa de marca única nunca vê o seletor — mostrá-lo com uma
 * opção só é pedir uma decisão que não existe, e é como um painel de grupo acaba parecendo
 * complicado para quem não é grupo.
 *
 * <h3>O menu esconde o que o papel não abre</h3>
 * Esconder não é autorizar: quem autoriza é o `@PreAuthorize` no servidor, e ele continua
 * valendo com o painel reescrito. O que se ganha aqui é não oferecer o que vai ser recusado.
 */
export function AppLayout() {
  const { sessao, sair, temPapel } = useAuth();
  const { marcaAtiva, escolherMarca } = useMarcaAtiva();
  const navegar = useNavigate();

  const marcas = useQuery({
    queryKey: ['marcas'],
    queryFn: () => api.get<Marca[]>('/console/brands'),
  });

  const lista = marcas.data ?? [];
  const daPessoa = sessao?.brandId ? lista.filter((m) => m.id === sessao.brandId) : lista;
  const mostrarSeletor = daPessoa.length > 1;
  const marca = daPessoa.find((m) => m.id === marcaAtiva) ?? daPessoa[0];

  return (
    <div className="app">
      <aside className="side">
        <div className="brand">
          <div className="wm">CONFIRA</div>
        </div>
        <nav>
          <div className="navlbl">Operação</div>
          <Item para="/indicadores" texto="Indicadores" />
          <Item para="/itens" texto="Itens" />
          <Item para="/sessoes" texto="Sessões" />
          {temPapel('COMPANY_ADMIN', 'COMPANY_SECURITY') && (
            <Item para="/alertas" texto="Alertas" />
          )}
          {temPapel('COMPANY_ADMIN', 'COMPANY_SECURITY', 'COMPANY_ANALYST') && (
            <Item para="/leituras-de-consumidor" texto="Leituras do consumidor" />
          )}
          {temPapel('COMPANY_SECURITY') && (
            <Item para="/marcacao-em-massa" texto="Marcação em massa" />
          )}

          <div className="navlbl">Cadastro</div>
          <Item para="/produtos" texto="Produtos" />
          <Item para="/lotes" texto="Lotes" />
          <Item para="/marcas" texto="Marcas" />
          <Item para="/checkpoints" texto="Checkpoints" />

          <div className="navlbl">Empresa</div>
          {temPapel('COMPANY_ADMIN') && <Item para="/unidades" texto="Unidades" />}
          {temPapel('COMPANY_ADMIN') && <Item para="/pessoas" texto="Pessoas e papéis" />}
          <Item para="/aparelhos" texto="Aparelhos" />
          {temPapel('COMPANY_ADMIN', 'COMPANY_SECURITY', 'COMPANY_ANALYST') && (
            <Item para="/ancoragem" texto="Ancoragem" />
          )}
          <Item para="/conta" texto="Conta" />
        </nav>
        <div className="foot">{sessao?.nomeFantasia ?? sessao?.razaoSocial}</div>
      </aside>

      <div className="main">
        <div className="top">
          {mostrarSeletor && (
            <label className="picker" title="A marca em que você está trabalhando">
              <span className="mk">{iniciais(marca?.name ?? '')}</span>
              <select
                value={marca?.id ?? ''}
                onChange={(e) => escolherMarca(e.target.value)}
                style={{ border: 0, background: 'transparent', fontSize: 13, fontWeight: 600 }}
              >
                {daPessoa.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="sp" />
          <div className="av" title={sessao?.nome}>
            {iniciais(sessao?.nome ?? '')}
          </div>
          <button
            className="btn btn-ghost"
            onClick={() => {
              sair();
              navegar('/entrar');
            }}
          >
            Sair
          </button>
        </div>
        <Outlet />
      </div>
    </div>
  );
}

function Item({ para, texto }: { para: string; texto: string }) {
  return (
    <NavLink to={para} className={({ isActive }) => (isActive ? 'nav on' : 'nav')}>
      {texto}
    </NavLink>
  );
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) {
    return '—';
  }
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}
